/**
 * Generic date primitives for ISO date string handling.
 *
 * Scope: Low-level utilities for parsing, formatting, and validating
 * ISO date strings (YYYY-MM-DD). These are generic and can be used
 * by any feature requiring date operations.
 *
 * IMPORTANT: Task-related date computations (getToday, getYesterday, getFriday)
 * use America/New_York timezone to match backend TASK_DATE_TZ. This ensures
 * metric counts and filter results are consistent regardless of user's timezone.
 */

import { differenceInDays, format, isValid, parse } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';

// Match backend TASK_DATE_TZ (app/defs/tasks.py)
export const TASK_TIMEZONE = 'America/New_York';

// ============================================================================
// ISO DATE STRING UTILITIES
// ============================================================================

/** Regex pattern for ISO date strings (YYYY-MM-DD) */
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Type guard to check if a value is a valid ISO date string (YYYY-MM-DD)
 */
export const isISODateString = (val: unknown): val is string =>
  typeof val === 'string' && ISO_DATE_PATTERN.test(val);

/**
 * Format a Date object to ISO date string (YYYY-MM-DD)
 * Uses LOCAL timezone to avoid UTC date shifts
 */
export const formatToISODate = (date: Date): string =>
  format(date, 'yyyy-MM-dd');

/**
 * Parse an ISO date string (YYYY-MM-DD) to Date object
 * Uses LOCAL timezone to avoid UTC date shifts
 */
export const parseISODate = (dateString: string): Date =>
  parse(dateString, 'yyyy-MM-dd', new Date());

// ============================================================================
// DATE COMPUTATIONS - Task-related dates in NY timezone
// ============================================================================

/**
 * Get today's date in NY timezone as ISO string (YYYY-MM-DD).
 * Uses NY timezone to match backend metric calculations.
 */
export const getToday = (): string => {
  return formatInTimeZone(new Date(), TASK_TIMEZONE, 'yyyy-MM-dd');
};

/**
 * Get yesterday's date in NY timezone as ISO string (YYYY-MM-DD).
 * Uses NY timezone to match backend metric calculations.
 */
export const getYesterday = (): string => {
  const nowInNY = toZonedTime(new Date(), TASK_TIMEZONE);
  nowInNY.setDate(nowInNY.getDate() - 1);
  return formatInTimeZone(nowInNY, TASK_TIMEZONE, 'yyyy-MM-dd');
};

/**
 * Get Friday for "Due This Week" filter in NY timezone.
 * Mon-Fri: this Friday. Sat-Sun: NEXT Friday (week resets on Saturday).
 * Matches backend build_due_by_friday_condition.
 */
export const getFriday = (): string => {
  const nowInNY = toZonedTime(new Date(), TASK_TIMEZONE);
  const currentDay = nowInNY.getDay(); // 0=Sunday, 1=Monday, ..., 5=Friday, 6=Saturday

  let daysUntilFriday: number;

  if (currentDay === 0) {
    // Sunday: next Friday is 5 days ahead
    daysUntilFriday = 5;
  } else if (currentDay === 6) {
    // Saturday: next Friday is 6 days ahead
    daysUntilFriday = 6;
  } else {
    // Monday-Friday: this Friday (or 0 if already Friday)
    daysUntilFriday = 5 - currentDay;
  }

  nowInNY.setDate(nowInNY.getDate() + daysUntilFriday);
  return formatInTimeZone(nowInNY, TASK_TIMEZONE, 'yyyy-MM-dd');
};

// ============================================================================
// DATE DISPLAY - Format dates in NY timezone for UI consistency
// ============================================================================

/**
 * Format a date string for display in NY timezone (e.g., "Dec 23, 2025").
 * Used by date columns to match backend filter behavior.
 */
export const formatDateInNY = (dateString: string): string => {
  return formatInTimeZone(new Date(dateString), TASK_TIMEZONE, 'MMM d, yyyy');
};

/**
 * Get the ISO date (YYYY-MM-DD) of a datetime string in NY timezone.
 * Used for overdue comparisons in date columns.
 */
export const getDateInNY = (dateString: string): string => {
  return formatInTimeZone(new Date(dateString), TASK_TIMEZONE, 'yyyy-MM-dd');
};

// ============================================================================
// DURATION UTILITIES - Calculate and format time elapsed since a date
// ============================================================================

/**
 * Calculate the number of days since a given date.
 * Returns null if the date is invalid or not provided.
 *
 * @example
 * getDaysSince('2024-01-01') // 365 (if today is 2025-01-01)
 * getDaysSince(null) // null
 */
export const getDaysSince = (
  dateString: string | null | undefined,
): number | null => {
  if (!dateString) return null;

  try {
    const date = new Date(dateString);
    if (!isValid(date)) return null;
    return differenceInDays(new Date(), date);
  } catch {
    return null;
  }
};

/**
 * Format days elapsed as a human-readable duration string.
 * Used for displaying "time in status", "days since last contact", etc.
 *
 * @example
 * formatDaysDuration(0)  // '< 1 day'
 * formatDaysDuration(1)  // '1 day'
 * formatDaysDuration(5)  // '5 days'
 * formatDaysDuration(null) // null
 */
export const formatDaysDuration = (days: number | null): string | null => {
  if (days === null) return null;

  if (days === 0) {
    return '< 1 day';
  }
  if (days === 1) {
    return '1 day';
  }
  return `${days} days`;
};

/**
 * Stale threshold configuration for duration-based highlighting.
 * Used to determine warning/error states for "time since" displays.
 */
export interface StaleThresholds {
  /** Days after which to show warning state (amber) */
  warning: number;
  /** Days after which to show error state (red) */
  error: number;
}

/** Default stale thresholds used across the app */
export const DEFAULT_STALE_THRESHOLDS: StaleThresholds = {
  warning: 14,
  error: 30,
};

/**
 * Determine the stale level based on days elapsed.
 * Returns 'error' | 'warning' | 'normal' for styling purposes.
 *
 * @example
 * getStaleLevel(5)   // 'normal'
 * getStaleLevel(20)  // 'warning'
 * getStaleLevel(45)  // 'error'
 */
export const getStaleLevel = (
  days: number | null,
  thresholds: StaleThresholds = DEFAULT_STALE_THRESHOLDS,
): 'error' | 'warning' | 'normal' => {
  if (days === null) return 'normal';
  if (days >= thresholds.error) return 'error';
  if (days >= thresholds.warning) return 'warning';
  return 'normal';
};
